import { isBooksyCom, useLogger } from '@booksy-com/vue-tools'
import moment from 'moment'
import Survicate from '@survicate/survicate-web-package/survicate_widget'

import { useCore } from '@/composables/core'
import { getBaseProperties } from '@/lib/analytics/helpers.js'
import { DEVICE } from '@/lib/analytics/consts.js'

import {
  camelToSnakeCase,
  transformAnalyticsDataToSurvicate,
} from './helpers'

import {
  WORKSPACE,
  EXCLUDED_ANALYTIC_EVENTS,
} from './consts'

import { useOnboardingStore } from '@/stores/onboarding'

const logger = useLogger('survicate')

type SurvicateStoreState = {
  app: {
    appType: string
    country: string
    fingerprint: string
    ios_fingerprint?: string | null
    webView: string
  }
  business: {
    business?: {
      active_from: string
      active_staffers_count: number
      boost_status: string
      primary_category_internal_name?: string
    } | null
  }
  routingConfig: { config: { country?: { code: string } } }
  identity: { user: unknown }
}

type VisitorTraits = {
  is_production: string
  user_id: string | null
  app_version: string
  device_type: string | null
  country_code: string | undefined
  user_role: string | null
  business_id: string | null
  business_admin_status: string | null
  business_category: string | undefined
  business_created: string | null
  staffer_number: number | null
  app_type: 'frontdesk'
  boost_status: string | null
}

const _getBaseVisitorTraits = (): VisitorTraits => {
  const { store } = useCore()
  const {
    app,
    business,
    routingConfig,
    identity,
  } = store.state as SurvicateStoreState
  const onboardingStore = useOnboardingStore()

  const businessForAnalytics = (business.business || onboardingStore.business) as
      SurvicateStoreState['business']['business'] | null | undefined

  const data = {
    appType: app.appType,
    business: businessForAnalytics,
    country: app.country,
    fingerprint: app.fingerprint,
    ios_fingerprint: app.ios_fingerprint,
    routingConfig: routingConfig.config,
    user: identity.user || onboardingStore.user,
    device: DEVICE[app.webView] || DEVICE.www,
  }

  const {
    logged_in_user_id,
    app_version,
    device_type,
    logged_in_user_role,
    business_id,
    business_admin_status,
  } = getBaseProperties(data)

  const visitorTraits: VisitorTraits = {
    is_production: isBooksyCom() ? 'true' : 'false',
    user_id: logged_in_user_id,
    app_version,
    device_type,
    country_code: data.routingConfig?.country.code,
    user_role: logged_in_user_role,
    business_id,
    business_admin_status,
    business_category: businessForAnalytics?.primary_category_internal_name,
    business_created: business.business ? moment(business.business.active_from).format('YYYY-MM-DD') : null,
    staffer_number: business.business ? business.business.active_staffers_count : null,
    app_type: 'frontdesk',
    boost_status: business.business ? business.business.boost_status : null,
  }

  return visitorTraits
}

let isInitialized = false

export function initSurvicate (): void {
  if (isInitialized) return

  Survicate.init({ workspaceKey: WORKSPACE })
  isInitialized = true

  logger.info('init')
}

export type UseSurvicateReturn = {
  updateVisitorIdentity: () => void
  invokeAnalyticsEvent: (name: string, data?: unknown) => void
}

export function useSurvicate (): UseSurvicateReturn {
  const updateVisitorIdentity = () => {
    Survicate.setVisitorTraits(_getBaseVisitorTraits())
    Survicate.retarget()

    logger.info('update visitor identity')
  }

  const invokeAnalyticsEvent = (name: string, data?: unknown) => {
    if (!name || EXCLUDED_ANALYTIC_EVENTS.some((excluded) => excluded === name)) return

    Survicate.invokeEvent(
      camelToSnakeCase(name, false),
      transformAnalyticsDataToSurvicate(data as Record<string, unknown> | undefined)
    )

    logger.info(`invoke analytics event - ${name}`)
  }

  return {
    updateVisitorIdentity,
    invokeAnalyticsEvent,
  }
}
